<template>
    <div class="doc-review-page">
      <div v-if="loading" class="state-message">
        <div class="spinner"></div>
        <p>Loading documents…</p>
      </div>
  
      <div v-else-if="data.length === 0" class="state-message empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
        <p>No documents pending review</p>
      </div>
  
      <div v-else class="doc-grid">
        <div
          v-for="(doc, index) in data"
          :key="index"
          class="doc-card"
          :class="{ 'is-approved': doc._status === 'approved', 'is-denied': doc._status === 'denied' }"
        >
          <div class="doc-image-wrap">
            <img
                v-if="doc._src"
                :src="doc._src"
                :alt="`Document ${index + 1}`"
                class="doc-image"
                @click="lightbox = doc._src"
            />
            <div v-else class="doc-image-placeholder">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
            </div>
  
            <div class="status-badge" v-if="doc._status">
              <span v-if="doc._status === 'approved'">✓ Approved</span>
              <span v-else>✕ Denied</span>
            </div>
          </div>
  
          <div class="doc-info">
            <p class="doc-name">{{ getDocName(doc) }}</p>
            <p class="doc-meta" v-if="getDocMeta(doc)">{{ getDocMeta(doc) }}</p>
            <div class="doc-tags">
                <a v-on:click="openJoborder(doc)"><span class="doc-tag">Job Order ID: 11-{{ doc.joborderid }}</span></a>
                <span class="doc-tag">Schedule ID: {{ doc.jobscheduleid }}</span>
                <span class="doc-tag">User: {{ doc.user }}</span>
            </div>
        </div>
  
          <div class="doc-actions" v-if="!doc._status">
            <template v-if="!doc._pending">
                <button class="btn btn-approve" @click="startAction(doc, index, 'approved')">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    Select
                </button>
                <button class="btn btn-deny" @click="startAction(doc, index, 'denied')">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    Deny
                </button>
            </template>
            <template v-else>
                <button
                  class="btn"
                  :class="doc._pending === 'approved' ? 'btn-confirm-approve' : 'btn-confirm-deny'"
                  @click="confirmAction(doc, index)"
                >
                    Confirm {{ doc._pending === 'approved' ? 'select' : 'deny' }}?
                </button>
                <button class="btn btn-cancel" @click="cancelPending(doc, index)">✕</button>
            </template>
          </div>
  
          <div class="doc-actions doc-actions--resolved" v-else>
            <button class="btn btn-undo" @click="undo(doc, index)">Undo</button>
          </div>
        </div>
      </div>

      <div class="lightbox" v-if="lightbox" @click="lightbox = null">
        <img :src="lightbox" class="lightbox-img" />
        <button class="lightbox-close" @click="lightbox = null">✕</button>
    </div>
    </div>
</template>
  
<script>
import { PAGES } from './../../../constants.js'
import api from '@/api'
import router from '@/router/index'

export default {
    data() {
        return {
            pageid: PAGES.MERGE_DOCUMENT_PAGE,
            data: [],
            loading: false,
            lightbox: null
        }
    },

    computed: {
        approvedCount() {
            return this.data.filter(d => d._status === 'approved' || d._status === 'denied').length
        },
        hasApproved() {
            return this.approvedCount > 0
        },
    },

    async created() {
        this.search()
    },

    methods: {
        search() {
            this.loading = true
            api.postData({
                type: 'search',
                data: { 
                    pageid: this.pageid,
                    data: []
                },
            }).then((res) => {
                if (
                    res.data != null &&
                    res.data.data != null &&
                    res.data.data.column_data != null &&
                    res.data.data.column_data.length > 0
                ) {
                    this.data = res.data.data.column_data.map(function(doc) {
                        return { 
                            id: doc[0], 
                            filepath: doc[1], 
                            joborderid: doc[2], 
                            jobscheduleid: doc[3], 
                            userid: doc[4], 
                            user: doc[5], 
                            _status: null, 
                            _src: null,
                            _pending: null }
                    })
                    // load images after data is set
                    this.data.forEach(function(doc, i) {
                        this.loadImage(doc, i)
                    }.bind(this))
                } else {
                    this.data = []
                }
                this.loading = false
            }).catch(() => {
                this.loading = false
            })
        },
  
        approve(doc, index) {
            this.merge(doc, 'approved')
        },
    
        deny(doc, index) {
            this.merge(doc, 'denied')
        },

        startAction(doc, index, action) {
            this.data.forEach(function(d, i) {
                if (i !== index) this.$set(this.data, i, Object.assign({}, d, { _pending: null }))
            }.bind(this))
            this.$set(this.data, index, Object.assign({}, doc, { _pending: action }))
        },
        confirmAction(doc, index) {
            this.merge(doc, doc._pending)
            this.$set(this.data, index, Object.assign({}, doc, { _pending: null }))
        },
        cancelPending(doc, index) {
            this.$set(this.data, index, Object.assign({}, doc, { _pending: null }))
        },
  
        merge(doc, status) {
            api.postData({
                type: 'insert',
                data: {
                    pageid: this.pageid,
                    data: { 
                        id: doc.id, 
                        filepath: doc.filepath, 
                        status: status,
                        userid: doc.userid,
                        jobscheduleid: doc.jobscheduleid
                    }
                }
            }).then((res) => {
                if (res.data.success && res.data.documentid) {
                    router.push('0502?q='+res.data.documentid);
                }else {
                    this.search()
                }
            })
        },
  
    
        loadImage(doc, index) {
            const filename = doc.filepath.split('/').pop()
            api.getImageBuffer(filename).then(function(res) {
                const blob = new Blob([res.data], { type: res.headers['content-type'] })
                const url = URL.createObjectURL(blob)
                this.$set(this.data, index, Object.assign({}, this.data[index], { _src: url }))
            }.bind(this)).catch(function(err) {
                console.log('image load error:', err)
            })
        },

        getImageUrl(doc) {
            return doc._src || null
        },
        getDocName(doc) {
            return 'Document ' + doc.id
        },
        getDocMeta(doc) {
            return doc.filepath.split('/').pop()
        },

        openJoborder(doc) {
            router.push('0301?q=11-'+doc.joborderid);
        },
    },
}
</script>
  
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@400;500;600&display=swap');

.doc-review-page {
    font-family: 'DM Sans', sans-serif;
    min-height: calc(100vh - 50px);
    background: #f5f4f0;
    color: #1a1a18;
    padding: 10px;
}

/* ── Header ── */
.page-header {
    display: flex;
    align-items: baseline;
    gap: 1rem;
    margin-bottom: 2.5rem;
    border-bottom: 2px solid #1a1a18;
    padding-bottom: 1rem;
}

.page-title {
    font-family: 'DM Serif Display', serif;
    font-size: 2rem;
    font-weight: 400;
    margin: 0;
    letter-spacing: -0.02em;
}

.doc-count {
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    background: #1a1a18;
    color: #f5f4f0;
    padding: 0.2rem 0.6rem;
    border-radius: 2rem;
}

/* ── States ── */
.state-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 5rem 0;
    color: #888;
}

.state-message p { margin: 0; font-size: 0.95rem; }

.spinner {
    width: 32px; height: 32px;
    border: 3px solid #ddd;
    border-top-color: #1a1a18;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Grid ── */
.doc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1.5rem;
}

/* ── Card ── */
.doc-card {
    background: #fff;
    border: 1.5px solid #e0dfd9;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: box-shadow 0.2s, transform 0.2s;
}

.doc-card:hover {
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
    transform: translateY(-2px);
}

.doc-card.is-approved {
    border-color: #2d7a4f;
    background: #f4fbf7;
}

.doc-card.is-denied {
    border-color: #c0392b;
    background: #fdf5f4;
    opacity: 0.75;
}

/* ── Image ── */
.doc-image-wrap {
    position: relative;
    aspect-ratio: 4/3;
    background: #eeecea;
    overflow: hidden;
}

.doc-image {
    width: 100%; height: 100%;
    object-fit: cover;
    display: block;
}

.doc-image-placeholder {
    width: 100%; height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #bbb;
}

.status-badge {
    position: absolute;
    top: 0.6rem; right: 0.6rem;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    padding: 0.25rem 0.6rem;
    border-radius: 2rem;
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(4px);
}

.is-approved .status-badge { color: #2d7a4f; }
.is-denied  .status-badge { color: #c0392b; }

/* ── Info ── */
.doc-info {
    padding: 0.85rem 1rem 0.5rem;
    flex: 1;
}

.doc-name {
    margin: 0 0 0.2rem;
    font-size: 0.9rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.doc-meta {
    margin: 0;
    font-size: 0.78rem;
    color: #888;
}

/* ── Actions ── */
.doc-actions {
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid #eeecea;
}

.doc-actions--resolved {
    justify-content: center;
}

/* ── Buttons ── */
.btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-family: inherit;
    font-size: 0.82rem;
    font-weight: 600;
    border: 1.5px solid transparent;
    border-radius: 5px;
    padding: 0.45rem 0.85rem;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s, opacity 0.15s;
}

.btn-approve {
    background: #2d7a4f;
    color: #fff;
    flex: 1;
}
.btn-approve:hover { background: #236040; }

.btn-deny {
    background: #fff;
    color: #c0392b;
    border-color: #c0392b;
    flex: 1;
}
.btn-deny:hover { background: #fdf0ef; }

.btn-undo {
    background: transparent;
    color: #666;
    border-color: #ccc;
    font-size: 0.78rem;
}
.btn-undo:hover { border-color: #999; color: #333; }

/* ── Footer / Merge ── */
.page-footer {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    padding: 1rem 2rem;
    background: rgba(245,244,240,0.9);
    backdrop-filter: blur(8px);
    border-top: 1.5px solid #e0dfd9;
    display: flex;
    justify-content: flex-end;
}

.btn-merge {
    background: #1a1a18;
    color: #f5f4f0;
    border-color: #1a1a18;
    font-size: 0.9rem;
    padding: 0.65rem 1.75rem;
    border-radius: 6px;
}
.btn-merge:hover:not(:disabled) { background: #333; }
.btn-merge:disabled { opacity: 0.35; cursor: not-allowed; }

.lightbox {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.92);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    cursor: zoom-out;
}

.lightbox-img {
    max-width: 90vw;
    max-height: 90vh;
    object-fit: contain;
    border-radius: 4px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6);
}

.lightbox-close {
    position: fixed;
    top: 1.25rem;
    right: 1.5rem;
    background: none;
    border: none;
    color: #fff;
    font-size: 1.5rem;
    cursor: pointer;
    opacity: 0.7;
    line-height: 1;
}

.lightbox-close:hover {
    opacity: 1;
}

.doc-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 6px;
}

.doc-tag {
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    padding: 2px 8px;
    border-radius: 99px;
    border: 1px solid #e0dfd9;
}
</style>


// WEBPACK FOOTER //
// src/components/edit/Merge_Documents.vue